import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SidebarService } from '../../services/sidebar.service';
import { CommonModule } from '@angular/common';
import { AppSidebarComponent } from '../app-sidebar/app-sidebar.component';
import { BackdropComponent } from '../backdrop/backdrop.component';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router, RouterModule } from '@angular/router';
import { AppHeaderComponent } from '../app-header/app-header.component';
import { distinctUntilChanged, filter, map, Observable } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { loadList } from '../../../lib/action';
import { AdministrationService } from '../../../services/administration/administration.service';
import { safeJsonParse } from '../../../services/common/common.service';
import { MasterdataService } from '../../../services/master-data/masterdata.service';
import { StatusColorService } from '../../../services/status-color/status-color.service';
import { isMapStudioUrl } from '../../utils/route.utils';

@Component({
  selector: 'app-layout',
  imports: [
    CommonModule,
    RouterModule,
    AppHeaderComponent,
    AppSidebarComponent,
    BackdropComponent
  ],
  templateUrl: './app-layout.component.html',
})

export class AppLayoutComponent implements OnInit {
  readonly isExpanded$;
  // readonly isHovered$;
  readonly isMobileOpen$;
  protected router = inject(Router)
  private activatedRoute = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  private adminService = inject(AdministrationService);
  private store = inject(Store<any>);
  private userDetails$: Observable<any>;
  private privilegeList$: Observable<any>;
  private isFullSizePage: boolean = false;
  private listOfFullSizePages: string[] = ['ai-editor/:id']

  isSuperuser: boolean = false;
  selectedApplicationId: any;
  private statusColorsRequested = false;

  constructor(public sidebarService: SidebarService, private masterdataService: MasterdataService, private statusColorService: StatusColorService) {
    this.isExpanded$ = this.sidebarService.isExpanded$;
    // this.isHovered$ = this.sidebarService.isHovered$;
    this.isMobileOpen$ = this.sidebarService.isMobileOpen$;
    this.userDetails$ = this.store.pipe(select(state => state.data.data));
    this.privilegeList$ = this.store.pipe(select(state => state?.lists?.privileges));
  }

  ngOnInit(): void {
    this.store.dispatch(loadList({ listType: 'profile' }));
    this.store.dispatch(loadList({ listType: 'privileges' }));
    this.store.dispatch(loadList({ listType: 'application' }));
    this.store.dispatch(loadList({ listType: 'country' }));
    this.userDetails$.subscribe(data => {
      this.isSuperuser = data?.is_superuser;
    });

    this.privilegeList$?.pipe(
      map(data => {
        const selectedApplicationId = this.selectedApplicationId = safeJsonParse(localStorage.getItem('selectedApplicationId'), null);
        if (selectedApplicationId) {
          return data?.[selectedApplicationId];
        }
        return null;
      }),
      distinctUntilChanged(),
      filter(appPrivileges => !!appPrivileges)
    ).subscribe((appPrivileges: any) => {
      this.adminService.setPrivilegeList(appPrivileges);
    });

    this.loadStatusColors();

    // Map Studio skips the colour fetch but is commonly deep-linked, so make
    // sure the pages the user navigates on to still get the live colours.
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        if (!this.statusColorsRequested) {
          this.loadStatusColors();
        }
      });
  }

  get containerClasses() {
    return [
      'flex-1',
      'transition-all',
      'duration-300',
      'ease-in-out',
      // (this.isExpanded$ || this.isHovered$) ? 'xl:ml-[290px]' : 'xl:ml-[90px]',
      this.isMobileOpen$ ? 'ml-0' : ''
    ];
  }

  buildUrlPattern(): string {
    const currentUrl = this.router.url.split('?')[0].split('#')[0]; // Remove query params and hash
    const params = this.activatedRoute.snapshot.paramMap;

    let pattern = currentUrl.startsWith('/') ? currentUrl.substring(1) : currentUrl;

    // Replace each param value with :paramName in order
    Object.entries(params).forEach(([key, value]) => {
      pattern = pattern.replace(String(value), `:${key}`);
    });

    return pattern;
  }

  // loadStatusColors() {
  //   this.masterdataService.getColorCode().subscribe({
  //     next: (res: any) => {
  //       this.statusColorService.setStatusColors(res.results);
  //     },
  //   });
  // }
  loadStatusColors() {
    // skip on library / map routes, Map Studio and on the root dashboard (/),
    // which use static chart colours and no live status colours.
    const currentUrl = this.router.url.split('?')[0].split('#')[0];
    if (currentUrl === '/' || currentUrl === '' || isMapStudioUrl(currentUrl) ||
      this.router.url.includes('/map/library') || this.router.url.includes('/map/map-error-code-list') || this.router.url.includes('/file-profile/standard')) {
      return;
    }
    this.statusColorsRequested = true;
    this.masterdataService.getColorCode().subscribe({
      next: (res: any) => {
        this.statusColorService.setStatusColors(res.results);
      },
    });
  }

}
